import { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { settingsAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { useAppSettings } from '../contexts/AppSettingsContext'
import './Settings.css'

const DEFAULT_PROVIDERS = [
  {
    provider: 'ntfy',
    enabled: true,
    server: 'https://push.cobrasoft.org',
    topic: 'ocpp-emu',
    token: '',
    tokenSet: false
  },
  {
    provider: 'pusher',
    enabled: false,
    appId: '',
    key: '',
    secret: '',
    secretSet: false,
    cluster: 'ap2',
    channel: 'ocpp-emulator'
  }
]

const DEFAULT_FORM = {
  appName: 'OCPP Emulator',
  browserTitle: 'OCPP Charging Station Emulator',
  smtp: {
    enabled: false,
    host: '',
    port: 587,
    username: '',
    password: '',
    fromEmail: '',
    fromName: '',
    encryption: 'starttls',
    passwordSet: false
  },
  pushNotification: {
    enabled: false,
    providers: DEFAULT_PROVIDERS
  },
  liveChatEmbed: '',
  emailOtp: {
    enabled: false,
    fallbackEmail: ''
  }
}

function mergeProviders(incoming) {
  const defaults = Object.fromEntries(DEFAULT_PROVIDERS.map((provider) => [provider.provider, { ...provider }]))
  const order = []
  const rows = Array.isArray(incoming) ? incoming : []
  rows.forEach((row) => {
    if (!defaults[row?.provider] || order.includes(row.provider)) return
    defaults[row.provider] = { ...defaults[row.provider], ...row, token: '', secret: '' }
    order.push(row.provider)
  })
  DEFAULT_PROVIDERS.forEach((provider) => {
    if (!order.includes(provider.provider)) order.push(provider.provider)
  })
  return order.map((kind) => defaults[kind])
}

function providerReady(provider) {
  if (provider.provider === 'ntfy') {
    return Boolean(provider.server && provider.topic && (provider.token || provider.tokenSet))
  }
  if (provider.provider === 'pusher') {
    return Boolean(provider.appId && provider.key && provider.cluster && provider.channel && (provider.secret || provider.secretSet))
  }
  return false
}

function providerRole(providers, index) {
  if (!providers[index]?.enabled || !providerReady(providers[index])) return ''
  const earlier = providers.slice(0, index).filter((provider) => provider.enabled && providerReady(provider)).length
  return earlier === 0 ? 'Primary' : 'Fallback'
}

function providerLabel(kind) {
  return kind === 'pusher' ? 'Pusher.com' : 'push.cobrasoft.org'
}

function mergeForm(data) {
  return {
    ...DEFAULT_FORM,
    ...data,
    smtp: { ...DEFAULT_FORM.smtp, ...data?.smtp },
    pushNotification: {
      ...DEFAULT_FORM.pushNotification,
      ...data?.pushNotification,
      providers: mergeProviders(data?.pushNotification?.providers)
    },
    emailOtp: { ...DEFAULT_FORM.emailOtp, ...data?.emailOtp }
  }
}

function Settings() {
  const { isAdmin } = useAuth()
  const { applySettings, refresh } = useAppSettings()
  const [form, setForm] = useState(DEFAULT_FORM)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [smtpTestTo, setSmtpTestTo] = useState('')
  const [testingPush, setTestingPush] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [testPopup, setTestPopup] = useState(null)

  useEffect(() => {
    const load = async () => {
      try {
        const response = await settingsAPI.get()
        setForm(mergeForm(response.data))
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load settings')
        setForm(DEFAULT_FORM)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  if (!isAdmin) {
    return <Navigate to="/" replace />
  }

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  const setNested = (parent, field, value) => {
    setForm((prev) => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }))
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    setSaving(true)
    setError('')
    setNotice('')
    try {
      const response = await settingsAPI.update({
        appName: form.appName,
        browserTitle: form.browserTitle,
        smtp: {
          enabled: form.smtp.enabled,
          host: form.smtp.host,
          port: Number(form.smtp.port) || 587,
          username: form.smtp.username,
          password: form.smtp.password,
          fromEmail: form.smtp.fromEmail,
          fromName: form.smtp.fromName,
          encryption: form.smtp.encryption
        },
        pushNotification: {
          enabled: form.pushNotification.enabled,
          providers: form.pushNotification.providers.map((provider) => ({
            provider: provider.provider,
            enabled: provider.enabled,
            server: provider.server,
            topic: provider.topic,
            token: provider.token,
            appId: provider.appId,
            key: provider.key,
            secret: provider.secret,
            cluster: provider.cluster,
            channel: provider.channel
          }))
        },
        liveChatEmbed: form.liveChatEmbed,
        emailOtp: {
          enabled: form.emailOtp.enabled,
          fallbackEmail: form.emailOtp.fallbackEmail
        }
      })
      const saved = mergeForm(response.data)
      setForm({
        ...saved,
        smtp: { ...saved.smtp, password: '' },
        pushNotification: {
          ...saved.pushNotification,
          providers: saved.pushNotification.providers.map((provider) => ({ ...provider, token: '', secret: '' }))
        }
      })
      applySettings({
        appName: saved.appName,
        browserTitle: saved.browserTitle,
        liveChatEmbed: saved.liveChatEmbed,
        emailOtpEnabled: saved.emailOtp.enabled
      })
      await refresh()
      setNotice('Settings saved.')
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save settings')
    } finally {
      setSaving(false)
    }
  }

  const setProvider = (index, field, value) => {
    setForm((prev) => {
      const providers = prev.pushNotification.providers.map((provider, i) => (
        i === index ? { ...provider, [field]: value } : provider
      ))
      return { ...prev, pushNotification: { ...prev.pushNotification, providers } }
    })
  }

  const moveProvider = (index, direction) => {
    setForm((prev) => {
      const next = index + direction
      const providers = [...prev.pushNotification.providers]
      if (next < 0 || next >= providers.length) return prev
      const [moved] = providers.splice(index, 1)
      providers.splice(next, 0, moved)
      return { ...prev, pushNotification: { ...prev.pushNotification, providers } }
    })
  }

  const showSystemNotification = async (title, message) => {
    if (!('Notification' in window)) return
    let permission = Notification.permission
    if (permission === 'default') {
      permission = await Notification.requestPermission()
    }
    if (permission === 'granted') {
      new Notification(title, { body: message })
    }
  }

  const handleTestPush = async () => {
    setTestingPush(true)
    setError('')
    setNotice('')
    const title = `${form.appName || 'OCPP Emulator'} test`
    const message = `This is a test notification from ${form.appName || 'OCPP Emulator'}.`
    try {
      const response = await settingsAPI.testPush()
      const data = response.data || {}
      const deliveredBy = providerLabel(data.provider)
      const detail = data.fallback
        ? `Primary failed${data.primaryError ? ` (${data.primaryError})` : ''}. Delivered by ${deliveredBy} as fallback.`
        : `Delivered by ${deliveredBy}.`
      setTestPopup({
        title: data.title || title,
        message: data.message || message,
        detail
      })
      await showSystemNotification(data.title || title, data.message || message)
    } catch (err) {
      setTestPopup({
        title: 'Push test failed',
        message: err.response?.data?.error || 'Push test failed',
        error: true
      })
    } finally {
      setTestingPush(false)
    }
  }

  const handleTestSmtp = async () => {
    const to = smtpTestTo.trim()
    if (!to) {
      setError('Enter an email address for the test')
      setNotice('')
      return
    }
    setTesting(true)
    setError('')
    setNotice('')
    try {
      const response = await settingsAPI.testSmtp(to)
      setNotice(`Test email sent to ${response.data.to}.`)
    } catch (err) {
      setError(err.response?.data?.error || 'SMTP test failed')
    } finally {
      setTesting(false)
    }
  }

  if (loading) {
    return <div className="settings-page"><div className="settings-page__loading">Loading settings…</div></div>
  }

  return (
    <div className="settings-page">
      <div className="page-header">
        <h2>Settings</h2>
      </div>

      {error && <div className="error-message">{error}</div>}
      {notice && <div className="info-message">{notice}</div>}

      <form className="settings-form" onSubmit={handleSubmit}>
        <section className="form-section">
          <h3>Application</h3>
          <div className="form-grid">
            <div className="form-field">
              <label htmlFor="appName">App name</label>
              <input
                id="appName"
                type="text"
                value={form.appName}
                onChange={(e) => setField('appName', e.target.value)}
                placeholder="OCPP Emulator"
              />
              <small>Shown in the header and login screen.</small>
            </div>
            <div className="form-field">
              <label htmlFor="browserTitle">Browser title</label>
              <input
                id="browserTitle"
                type="text"
                value={form.browserTitle}
                onChange={(e) => setField('browserTitle', e.target.value)}
                placeholder="OCPP Charging Station Emulator"
              />
              <small>Used as the browser tab title.</small>
            </div>
          </div>
        </section>

        <section className="form-section">
          <h3>SMTP</h3>
          <div className="form-field form-field--checkboxes">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={form.smtp.enabled}
                onChange={(e) => setNested('smtp', 'enabled', e.target.checked)}
              />
              Enable SMTP
            </label>
          </div>
          <div className="form-grid">
            <div className="form-field">
              <label htmlFor="smtpHost">Host</label>
              <input
                id="smtpHost"
                type="text"
                value={form.smtp.host}
                onChange={(e) => setNested('smtp', 'host', e.target.value)}
                placeholder="smtp.example.com"
              />
            </div>
            <div className="form-field">
              <label htmlFor="smtpPort">Port</label>
              <input
                id="smtpPort"
                type="number"
                min="1"
                value={form.smtp.port}
                onChange={(e) => setNested('smtp', 'port', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label htmlFor="smtpEncryption">Encryption</label>
              <select
                id="smtpEncryption"
                value={form.smtp.encryption}
                onChange={(e) => setNested('smtp', 'encryption', e.target.value)}
              >
                <option value="starttls">STARTTLS</option>
                <option value="tls">TLS</option>
                <option value="none">None</option>
              </select>
            </div>
            <div className="form-field">
              <label htmlFor="smtpUsername">Username</label>
              <input
                id="smtpUsername"
                type="text"
                value={form.smtp.username}
                onChange={(e) => setNested('smtp', 'username', e.target.value)}
                autoComplete="off"
              />
            </div>
            <div className="form-field">
              <label htmlFor="smtpPassword">Password</label>
              <input
                id="smtpPassword"
                type="password"
                value={form.smtp.password}
                onChange={(e) => setNested('smtp', 'password', e.target.value)}
                placeholder={form.smtp.passwordSet ? 'Leave blank to keep current password' : 'SMTP password'}
                autoComplete="new-password"
              />
            </div>
            <div className="form-field">
              <label htmlFor="smtpFromEmail">From email</label>
              <input
                id="smtpFromEmail"
                type="email"
                value={form.smtp.fromEmail}
                onChange={(e) => setNested('smtp', 'fromEmail', e.target.value)}
                placeholder="noreply@example.com"
              />
            </div>
            <div className="form-field">
              <label htmlFor="smtpFromName">From name</label>
              <input
                id="smtpFromName"
                type="text"
                value={form.smtp.fromName}
                onChange={(e) => setNested('smtp', 'fromName', e.target.value)}
                placeholder="OCPP Emulator"
              />
            </div>
          </div>
          <div className="settings-actions settings-actions--inline">
            <label className="smtp-test-to" htmlFor="smtpTestTo">
              Test email
              <input
                id="smtpTestTo"
                type="email"
                value={smtpTestTo}
                onChange={(e) => setSmtpTestTo(e.target.value)}
                placeholder="you@example.com"
                autoComplete="off"
              />
            </label>
            <button type="button" className="btn btn--sm btn--secondary" onClick={handleTestSmtp} disabled={testing || !form.smtp.enabled}>
              {testing ? 'Sending…' : 'Send test email'}
            </button>
          </div>
        </section>

        <section className="form-section">
          <h3>Push notifications</h3>
          <div className="form-field form-field--checkboxes">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={form.pushNotification.enabled}
                onChange={(e) => setNested('pushNotification', 'enabled', e.target.checked)}
              />
              Enable push notifications
            </label>
          </div>
          <p className="settings-hint">The first configured provider sends the notification. If that send fails, the next configured provider is the fallback.</p>
          {form.pushNotification.providers.map((provider, index) => {
            const role = providerRole(form.pushNotification.providers, index)
            return (
              <div className="push-provider" key={provider.provider}>
                <div className="push-provider__head">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={provider.enabled}
                      onChange={(e) => setProvider(index, 'enabled', e.target.checked)}
                    />
                    {providerLabel(provider.provider)}
                  </label>
                  <div className="push-provider__order">
                    {role && <span className="push-provider__role">{role}</span>}
                    <button type="button" className="btn btn--sm btn--secondary" onClick={() => moveProvider(index, -1)} disabled={index === 0}>Up</button>
                    <button type="button" className="btn btn--sm btn--secondary" onClick={() => moveProvider(index, 1)} disabled={index === form.pushNotification.providers.length - 1}>Down</button>
                  </div>
                </div>
                {provider.provider === 'ntfy' ? (
                  <div className="form-grid">
                    <div className="form-field">
                      <label htmlFor={`ntfy-server-${index}`}>Server</label>
                      <input id={`ntfy-server-${index}`} type="text" value={provider.server} onChange={(e) => setProvider(index, 'server', e.target.value)} />
                    </div>
                    <div className="form-field">
                      <label htmlFor={`ntfy-topic-${index}`}>Topic</label>
                      <input id={`ntfy-topic-${index}`} type="text" value={provider.topic} onChange={(e) => setProvider(index, 'topic', e.target.value)} />
                    </div>
                    <div className="form-field form-field--wide">
                      <label htmlFor={`ntfy-token-${index}`}>Access token</label>
                      <input
                        id={`ntfy-token-${index}`}
                        type="password"
                        value={provider.token}
                        onChange={(e) => setProvider(index, 'token', e.target.value)}
                        placeholder={provider.tokenSet ? 'Leave blank to keep current token' : 'tk_…'}
                        autoComplete="new-password"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="form-grid">
                    <div className="form-field">
                      <label htmlFor={`pusher-app-${index}`}>App ID</label>
                      <input id={`pusher-app-${index}`} type="text" value={provider.appId} onChange={(e) => setProvider(index, 'appId', e.target.value)} />
                    </div>
                    <div className="form-field">
                      <label htmlFor={`pusher-key-${index}`}>Key</label>
                      <input id={`pusher-key-${index}`} type="text" value={provider.key} onChange={(e) => setProvider(index, 'key', e.target.value)} />
                    </div>
                    <div className="form-field">
                      <label htmlFor={`pusher-secret-${index}`}>Secret</label>
                      <input
                        id={`pusher-secret-${index}`}
                        type="password"
                        value={provider.secret}
                        onChange={(e) => setProvider(index, 'secret', e.target.value)}
                        placeholder={provider.secretSet ? 'Leave blank to keep current secret' : 'Pusher secret'}
                        autoComplete="new-password"
                      />
                    </div>
                    <div className="form-field">
                      <label htmlFor={`pusher-cluster-${index}`}>Cluster</label>
                      <input id={`pusher-cluster-${index}`} type="text" value={provider.cluster} onChange={(e) => setProvider(index, 'cluster', e.target.value)} placeholder="ap2" />
                    </div>
                    <div className="form-field">
                      <label htmlFor={`pusher-channel-${index}`}>Channel</label>
                      <input id={`pusher-channel-${index}`} type="text" value={provider.channel} onChange={(e) => setProvider(index, 'channel', e.target.value)} />
                    </div>
                  </div>
                )}
              </div>
            )
          })}
          <div className="settings-actions settings-actions--inline">
            <button type="button" className="btn btn--sm btn--secondary" onClick={handleTestPush} disabled={testingPush || !form.pushNotification.enabled}>
              {testingPush ? 'Sending…' : 'Send test notification'}
            </button>
            <small>Uses the saved settings, sends a test, and opens the notification popup.</small>
          </div>
        </section>

        <section className="form-section">
          <h3>Live chat embed</h3>
          <div className="form-field form-field--wide">
            <label htmlFor="liveChatEmbed">Embed code</label>
            <textarea
              id="liveChatEmbed"
              rows="6"
              value={form.liveChatEmbed}
              onChange={(e) => setField('liveChatEmbed', e.target.value)}
              placeholder="Paste Tawk.to, Crisp, Intercom, or similar widget script"
            />
            <small>Optional. Paste the provider script. It is injected on login and signed-in pages.</small>
          </div>
        </section>

        <section className="form-section">
          <h3>Second factor email OTP</h3>
          <div className="form-field form-field--checkboxes">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={form.emailOtp.enabled}
                onChange={(e) => setNested('emailOtp', 'enabled', e.target.checked)}
              />
              Enable email OTP after password login
            </label>
          </div>
          <div className="form-grid">
            <div className="form-field">
              <label htmlFor="otpFallback">Fallback OTP email</label>
              <input
                id="otpFallback"
                type="email"
                value={form.emailOtp.fallbackEmail}
                onChange={(e) => setNested('emailOtp', 'fallbackEmail', e.target.value)}
                placeholder="admin@example.com"
              />
              <small>Used when a user has no email in the auth config. SMTP must be enabled.</small>
            </div>
          </div>
        </section>

        <div className="settings-actions">
          <button type="submit" className="btn btn--primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save settings'}
          </button>
        </div>
      </form>

      {testPopup && (
        <div className="modal-overlay modal-overlay--animated" onClick={() => setTestPopup(null)}>
          <div
            className="modal modal--sm modal--animated"
            role="dialog"
            aria-modal="true"
            aria-labelledby="push-test-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="modal__header">
              <h2 className="modal__title" id="push-test-title">Test notification</h2>
              <button type="button" className="modal__close" onClick={() => setTestPopup(null)} aria-label="Close">×</button>
            </div>
            <div className="modal__body">
              <div className={`push-test-card${testPopup.error ? ' push-test-card--error' : ''}`}>
                <strong>{testPopup.title}</strong>
                <p>{testPopup.message}</p>
                {testPopup.detail && <small>{testPopup.detail}</small>}
              </div>
            </div>
            <div className="modal__footer">
              <button type="button" className="btn btn--primary" onClick={() => setTestPopup(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Settings
